import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "@/i18n";
import { Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SITE_URL, socialImageMeta, breadcrumbLd } from "@/lib/seo";

const TITLE = "Advertise on BidNova Auctions | Partner Placements";
const DESCRIPTION = "Reach thousands of UK trading card collectors with sponsored homepage banners, Featured Shop placements and Official Partner status on BidNova Auctions.";
const URL = `${SITE_URL}/advertise`;

export const Route = createFileRoute("/advertise")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      ...socialImageMeta(),
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbLd([
        { name: "Home", path: "/" },
        { name: "Advertise", path: "/advertise" },
      ]),
    ],
  }),
  component: AdvertisePage,
});

type Placement = { key: string; name: string; tagline: string; benefits: readonly string[]; recommended?: boolean };

function usePlacements(t: (key: string, fallback: string) => string): readonly Placement[] {
  return [
    {
      key: "banner",
      name: t("advertise.placements.banner.name", "Homepage Sponsored Banner"),
      tagline: t("advertise.placements.banner.tagline", "Premium hero placement in front of every visitor."),
      benefits: [
        t("advertise.placements.banner.b1", "Above-the-fold placement on the homepage"),
        t("advertise.placements.banner.b2", "Clearly labelled as Sponsored"),
        t("advertise.placements.banner.b3", "Transparent impression & click reporting"),
        t("advertise.placements.banner.b4", "Rotates with a small curated set of partners"),
      ],
    },
    {
      key: "featuredShop",
      name: t("advertise.placements.featuredShop.name", "Featured Shop"),
      tagline: t("advertise.placements.featuredShop.tagline", "Get your shop highlighted to serious collectors."),
      benefits: [
        t("advertise.placements.featuredShop.b1", "Featured Shops grid on the homepage"),
        t("advertise.placements.featuredShop.b2", "Direct link to your BidNova seller profile"),
        t("advertise.placements.featuredShop.b3", "Verified badge on your public profile"),
        t("advertise.placements.featuredShop.b4", "Priority placement in category pages"),
      ],
      recommended: true,
    },
    {
      key: "officialPartner",
      name: t("advertise.placements.officialPartner.name", "Official Partner"),
      tagline: t("advertise.placements.officialPartner.tagline", "Join the BidNova Official Partner Network."),
      benefits: [
        t("advertise.placements.officialPartner.b1", "Logo in the Official Partners carousel"),
        t("advertise.placements.officialPartner.b2", "Dedicated public partner profile page"),
        t("advertise.placements.officialPartner.b3", "Cross-promotion in newsletters & socials"),
        t("advertise.placements.officialPartner.b4", "Long-term brand association with BidNova"),
      ],
    },
    {
      key: "eventSponsorship",
      name: t("advertise.placements.eventSponsorship.name", "Event & Custom Sponsorship"),
      tagline: t("advertise.placements.eventSponsorship.tagline", "Bespoke campaigns and event tie-ins."),
      benefits: [
        t("advertise.placements.eventSponsorship.b1", "Tournament, event and prize sponsorships"),
        t("advertise.placements.eventSponsorship.b2", "Custom homepage takeovers"),
        t("advertise.placements.eventSponsorship.b3", "Integrated editorial features"),
        t("advertise.placements.eventSponsorship.b4", "Tailored to your brand & audience"),
      ],
    },
  ];
}

function AdvertisePage() {
  const { t } = useTranslation("community");
  const PLACEMENTS = usePlacements(t);
  const FAQS = useFaqs(t);
  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-16">
      <div className="text-center">
        <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-[11px] uppercase tracking-[0.2em] text-gold">
          <Sparkles className="h-3.5 w-3.5" /> {t("advertise.eyebrow", "Partner Programme")}
        </div>
        <h1 className="mt-6 font-display text-4xl text-white sm:text-6xl">{t("advertise.title", "Advertise on BidNova")}</h1>
        <p className="mx-auto mt-4 max-w-2xl text-muted-foreground sm:text-lg">
          {t("advertise.subtitlePrefix", "Get in front of the UK's most engaged trading card community. During our launch phase, all advertising placements are ")}
          <span className="text-white font-medium">{t("advertise.subtitleHighlight", "complimentary")}</span>
          {t("advertise.subtitleSuffix", " and curated by the BidNova team.")}
        </p>
        <div className="mx-auto mt-5 inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-1.5 text-xs uppercase tracking-[0.18em] text-emerald-300">
          {t("advertise.freeBadge", "Free during launch · No credit card required")}
        </div>
      </div>

      <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {PLACEMENTS.map((p) => (
          <div
            key={p.key}
            className={
              "relative flex flex-col rounded-2xl border p-6 backdrop-blur " +
              (p.recommended
                ? "border-gold/60 bg-gradient-to-br from-purple-950/50 via-card/70 to-black shadow-[0_0_50px_-15px_rgba(212,175,55,0.55)]"
                : "border-white/10 bg-card/40")
            }
          >
            {p.recommended && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gold px-3 py-1 text-[11px] font-semibold text-black">
                {t("advertise.mostPopular", "Most popular")}
              </span>
            )}
            <h2 className="font-display text-2xl text-white">{p.name}</h2>
            {p.tagline && <p className="mt-1 text-sm text-muted-foreground">{p.tagline}</p>}
            <ul className="mt-6 space-y-2.5 text-sm text-muted-foreground">
              {p.benefits.map((b) => (
                <li key={b} className="flex items-start gap-2">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="flex-1" />
            <Button
              asChild
              className={
                "mt-8 w-full " +
                (p.recommended ? "bg-gold text-black hover:bg-gold/90" : "bg-white/10 hover:bg-white/20")
              }
            >
              <Link to="/partners/apply">{t("advertise.applyNow", "Apply now")}</Link>
            </Button>
          </div>
        ))}
      </div>

      <p className="mt-6 text-center text-xs text-muted-foreground">
        {t("advertise.disclaimer", "Placements are reviewed manually by our team and clearly labelled as \u201cSponsored\u201d. BidNova does not guarantee specific views, clicks or sales outcomes.")}
      </p>

      <section className="mt-20">
        <h2 className="font-display text-3xl text-white">{t("advertise.faqTitle", "Frequently asked")}</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {FAQS.map((f) => (
            <div key={f.key} className="rounded-xl border border-white/10 bg-card/30 p-5">
              <h3 className="font-medium text-white">{f.q}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-16 rounded-2xl border border-gold/30 bg-gradient-to-br from-purple-950/40 via-black to-black p-8 text-center">
        <h3 className="font-display text-2xl text-white">{t("advertise.ctaTitle", "Prefer to talk it through?")}</h3>
        <p className="mt-2 text-muted-foreground">{t("advertise.ctaSubtitle", "Tell us about your business and we'll come back with a tailored proposal.")}</p>
        <Button asChild size="lg" className="mt-6 bg-gold text-black hover:bg-gold/90">
          <Link to="/partners/apply">{t("advertise.ctaButton", "Start an application")}</Link>
        </Button>
      </div>
    </div>
  );
}

function useFaqs(t: (key: string, fallback: string) => string) {
  return [
    { key: "q1", q: t("advertise.faqs.q1.q", "How much does it cost?"), a: t("advertise.faqs.q1.a", "Advertising placements on BidNova are currently free while we grow the partner network. Our team will let you know well in advance if that ever changes.") },
    { key: "q2", q: t("advertise.faqs.q2.q", "Where will my advert appear?"), a: t("advertise.faqs.q2.a", "Placements include the homepage sponsored slot, the Featured Shops grid, the Official Partners carousel, or a bespoke campaign agreed with our team.") },
    { key: "q3", q: t("advertise.faqs.q3.q", "How do you count impressions and clicks?"), a: t("advertise.faqs.q3.a", "Impressions are counted only when the placement enters the visible viewport, and clicks are recorded server-side with duplicate protection. No personal identifiers are stored.") },
    { key: "q4", q: t("advertise.faqs.q4.q", "Do you offer bespoke sponsorship?"), a: t("advertise.faqs.q4.a", "Yes — event sponsorships, custom homepage takeovers and integrated campaigns are all available. Mention your idea in the application form.") },
  ] as const;
}
